import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, taskAPI, visitAPI, clientAPI } from '../lib/api';
import { formatDate, formatDateTime, getStatusColor, getPhaseLabel } from '../lib/utils';
import {
  CheckSquare,
  Calendar,
  Building2,
  Clock,
  ArrowRight,
  Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import StickyNotesWidget from '../components/StickyNotesWidget';

export const StaffDashboard = () => {
  const { user } = useAuth();
  usePageTitle('Dashboard');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, tasksRes, visitsRes, clientsRes] = await Promise.all([
        dashboardAPI.getStats(),
        taskAPI.getAll({ assigned_to: user?.id }),
        visitAPI.getAll(),
        clientAPI.getAll(),
      ]);

      setStats(statsRes.data);
      setTasks(tasksRes.data);
      setVisits(visitsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await taskAPI.update(taskId, { status: newStatus });
      toast.success('Task updated!');
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const pendingTasks = tasks.filter((t) => t.status !== 'completed');
  const upcomingVisits = visits.filter((v) => v.status === 'scheduled');
  const todayVisits = visits.filter((v) => {
    const visitDate = new Date(v.visit_date);
    const today = new Date();
    return visitDate.toDateString() === today.toDateString();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="staff-dashboard">
      {/* Welcome Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-blue-100">
            Here's your work summary for today
          </p>
          {todayVisits.length > 0 && (
            <div className="mt-4 p-3 bg-white/10 rounded-lg backdrop-blur-sm">
              <p className="text-sm text-white/80">Today's Visits</p>
              <p className="font-semibold">
                {todayVisits.length} visit{todayVisits.length > 1 ? 's' : ''} scheduled
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Notes — pinned to top */}
      <StickyNotesWidget />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50 cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-pending-tasks" onClick={() => navigate('/tasks')}>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <CheckSquare className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{stats?.pending_tasks || pendingTasks.length}</p>
              <p className="text-sm text-amber-500 font-medium">Pending Tasks</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50 cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-upcoming-visits" onClick={() => navigate('/visits')}>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <Calendar className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{stats?.upcoming_visits || upcomingVisits.length}</p>
              <p className="text-sm text-blue-500 font-medium">Upcoming Visits</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50 cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-clients" onClick={() => navigate('/clients')}>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 rounded-xl">
              <Building2 className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">{stats?.active_clients || clients.length}</p>
              <p className="text-sm text-emerald-500 font-medium">My Clients</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-violet-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-violet-100 rounded-xl">
              <CheckSquare className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-violet-700">
                {tasks.filter((t) => t.status === 'completed').length}
              </p>
              <p className="text-sm text-violet-500 font-medium">Completed</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Tasks */}
        <Card data-testid="my-tasks-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">My Tasks</CardTitle>
              <CardDescription>Tasks assigned to you</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {pendingTasks.length > 0 ? (
              <div className="space-y-3">
                {pendingTasks.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/tasks?id=${task.id}`)}
                    data-testid={`dashboard-task-${task.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleTaskStatusChange(task.id, 'completed')}
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{task.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {clients.find((c) => c.id === task.client_id)?.organization_name || 'Unknown'}
                          </Badge>
                          {task.due_date && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(task.due_date)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge className={getStatusColor(task.status)}>
                      {task.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-muted-foreground">
                No pending tasks. Great job!
              </p>
            )}
          </CardContent>
        </Card>

        {/* My Visits */}
        <Card data-testid="my-visits-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Upcoming Visits</CardTitle>
              <CardDescription>Your scheduled visits</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/visits')}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {upcomingVisits.length > 0 ? (
              <div className="space-y-3">
                {upcomingVisits.slice(0, 5).map((visit) => (
                  <div
                    key={visit.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/visits?id=${visit.id}`)}
                    data-testid={`dashboard-visit-${visit.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Calendar className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{visit.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {clients.find((c) => c.id === visit.client_id)?.organization_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDateTime(visit.visit_date)}
                        </p>
                      </div>
                    </div>
                    <Badge className={getStatusColor(visit.status)}>
                      {visit.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-muted-foreground">
                No upcoming visits scheduled
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* My Clients */}
      <Card data-testid="my-clients-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">My Clients</CardTitle>
            <CardDescription>Clients assigned to you</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/clients')}>
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {clients.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.slice(0, 6).map((client) => (
                <div
                  key={client.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/clients?id=${client.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{client.organization_name}</p>
                      <p className="text-sm text-muted-foreground">{client.city}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <Badge variant="outline">{getPhaseLabel(client.current_phase)?.split(' ')[0]}</Badge>
                    <Badge className={client.is_approved ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                      {client.is_approved ? 'Active' : 'Pending'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">
              No clients assigned yet
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffDashboard;
